import React from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import Heading from 'components/Luxkit/Typography/Heading'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import LineCheckIcon from 'components/Luxkit/Icons/line/LineCheckIcon'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import LineArrowDownIcon from 'components/Luxkit/Icons/line/LineArrowDownIcon'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import OfferUrgencyRoomCount from 'components/Common/Labels/OfferUrgencyRoomCount'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import LineArrowsRightDownIcon from 'components/Luxkit/Icons/line/LineArrowsRightDownIcon'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import { EmptyArray } from 'lib/array/arrayUtils'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import MobileMultipleRoomsCarouselPackageOption from '../MobileMultipleRoomsCarouselPackageOption'

const RoomOptionContainer = styled.div`
  padding: ${rem(20)};
`

interface Props {
  checkIn?: string;
  availableRoomCount?: number;
  packageOptions: Array<OfferPackageOptionView>;
  selectedPackageView: OfferPackageOptionView;
  onRoomSelect: (packageOption: App.Package) => void;
}

function MobileMultipleRoomsChangeRoomOption(props: Props) {
  const { packageOptions = EmptyArray, checkIn, availableRoomCount, selectedPackageView, onRoomSelect } = props
  const pkg = packageOptions[0]?.package as App.HotelPackage

  const availablePackageOptions = packageOptions.filter(option => (option.availableRooms ?? 0) > 0)
  const hasSelectedPackageView = packageOptions.some(option => option.package.uniqueKey === selectedPackageView.package.uniqueKey)
  const croPopularOptionBookingRemaining = !!useOptimizelyExperiment(OptimizelyExperiments.croPopularOptionBookingRemaining, !!availableRoomCount && !!checkIn)

  if (!pkg) {
    return null
  }

  return (
    <Pane>
      {hasSelectedPackageView && (
        <Sheet background="highlight-primary" padding="4 20">
          <CaptionTextBlock variant="medium" startIcon={<LineArrowDownIcon />}>Your current selection</CaptionTextBlock>
        </Sheet>
      )}
      <AccommodationRoomTile roomPackage={pkg}>
        <AccommodationRoomTileImage roomPackage={pkg} />
      </AccommodationRoomTile>
      <RoomOptionContainer>
        <Group direction="vertical" gap={16}>
          <Group direction="vertical" gap={12}>
            <Heading variant="heading4">{pkg.roomType?.name}</Heading>
            <LabelGroup>
              {checkIn && !croPopularOptionBookingRemaining && <RoomsRemainingLabel roomCount={packageOptions[0].availableRooms ?? 0} threshold="rate" />}
              {checkIn && croPopularOptionBookingRemaining && availableRoomCount && <OfferUrgencyRoomCount availableRoomCount={availableRoomCount}/> }
              <BookingsCountLabel bookings={pkg.roomType?.stats.booked ?? 0} threshold="room" />
            </LabelGroup>
            <LabelGroup>
              {!!pkg.roomType?.sizeSqm && <StructureLabel variant="light" startIcon={<LineArrowsRightDownIcon />}>{pkg.roomType.sizeSqm} m&sup2;</StructureLabel>}
              {!!pkg.roomType?.numberOfBedrooms && <StructureLabel variant="light" startIcon={<LineBedIcon />}>{pkg.roomType.numberOfBedrooms} bedrooms</StructureLabel>}
            </LabelGroup>
          </Group>
          <CardCarousel snap="start" width={availablePackageOptions.length === 1 ? '100%' : 'auto'} gap={12}>
            {availablePackageOptions.map((packageView, index) => {
              const isSelected = packageView.package.uniqueKey === selectedPackageView.package.uniqueKey
              return <MobileMultipleRoomsCarouselPackageOption
                key={packageView.package.uniqueKey}
                packageView={packageView}
                isSelected={isSelected}
                childrenCtaDirection="horizontal"
                packagePosition={availablePackageOptions.length > 1 ? index + 1 : undefined}
              >
                <>
                  {isSelected && <TextButton kind="primary" startIcon={<LineCheckIcon />}>Selected</TextButton>}
                  {!isSelected && <TextButton kind="secondary" onClick={() => onRoomSelect(packageView.package)}>Select</TextButton>}
                </>
              </MobileMultipleRoomsCarouselPackageOption>
            })}
          </CardCarousel>
        </Group>
      </RoomOptionContainer>
    </Pane>
  )
}

type MappedProps = Pick<Props, 'checkIn'>

export default withOfferPageAndReduxState((state): MappedProps => ({
  checkIn: state.checkInDate,
}), MobileMultipleRoomsChangeRoomOption)
